import React, { useState, useRef } from "react";
import { cn } from "../../lib/utils";
import { formatNewspaperDate, getVolumeIssue } from "../../lib/utils";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { createMailingList } from "../mailing_lists/shared";
import Recaptcha from "../shared/Recaptcha";

export function Hero({ className, siteKey, recaptchaEnabled }) {
  const recaptchaRef = useRef(null);
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const { volume, issue } = getVolumeIssue();
  const publicationDate = formatNewspaperDate();

  const handleRecaptchaToken = (token) => {
    setRecaptchaToken(token);
    submitForm(token);
  };

  const submitForm = (token = null) => {
    if (email) {
      createMailingList(email, () => {
        setIsSubmitted(true);
      }, () => {
        setRecaptchaToken(null); // Reset token on failure so recaptcha re-executes on retry
      }, token);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (recaptchaEnabled) {
      if (!recaptchaToken) {
        recaptchaRef.current.execute();
        return;
      }
    }

    submitForm(recaptchaToken);
  };

  return (
    <section
      className={cn(
        "relative pt-12 pb-4 md:pt-16 md:pb-6 px-4",
        "bg-paper",
        "overflow-hidden",
        className
      )}
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Subtle gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-paper via-paper to-paper-dark/30" />
      </div>

      <div className="relative max-w-4xl mx-auto text-center">
        {/* Publication Info */}
        <div className="animate-on-load animate-fade-in stagger-1">
          <div className="flex items-center justify-center gap-3 md:gap-6 text-ink-muted font-sans text-sm mb-8">
            <span className="tracking-wider">Volume {volume}</span>
            <span className="w-1 h-1 bg-gold rounded-full" />
            <span className="tracking-wider">Issue {issue}</span>
            <span className="w-1 h-1 bg-gold rounded-full" />
            <span className="tracking-wider">{publicationDate}</span>
          </div>
        </div>

        {/* Main Headline */}
        <div className="animate-on-load animate-ink-bleed stagger-2">
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-ink leading-tight mb-6">
            Your Weekly Intelligence on
            <br />
            <span className="text-gold">Arabian-Nubian Mining</span>
          </h2>
        </div>

        {/* Subheadline */}
        <div className="animate-on-load animate-fade-in-up stagger-3">
          <p className="font-body text-lg md:text-xl text-ink-secondary max-w-2xl mx-auto mb-10 leading-relaxed">
            Gold operations, investment deals, policy changes, and market
            analysis from the MENA and Africa regions. Delivered to your inbox
            every week.
          </p>
        </div>

        {/* Subscription Form */}
        <div className="animate-on-load animate-fade-in-up stagger-4">
          {isSubmitted ? (
            <div className="deco-frame inline-block px-8 py-6 bg-gold-muted">
              <p className="font-serif text-xl text-ink font-semibold mb-2">
                Welcome to The Brief!
              </p>
              <p className="font-body text-ink-secondary">
                Check your inbox for a confirmation email.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-lg mx-auto"
            >
              <div className="w-full sm:flex-1">
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="text-center sm:text-left"
                />
              </div>
              <Button type="submit" variant="primary" size="lg" onClick={handleSubmit}>
                Subscribe Free
              </Button>
            </form>
          )}

          {/* Trust signals */}
          {!isSubmitted && (
            <p className="mt-4 font-sans text-sm text-ink-light">
              No credit card required &bull; Unsubscribe anytime
            </p>
          )}
        </div>

        {/* Decorative Art Deco element */}
        <div className="mt-8 flex items-center justify-center">
          <div className="flex items-center gap-3">
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-border-dark" />
            <div className="w-3 h-3 border-2 border-gold rotate-45" />
            <div className="w-2 h-2 bg-gold rotate-45" />
            <div className="w-3 h-3 border-2 border-gold rotate-45" />
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-border-dark" />
          </div>
        </div>
      </div>

      {recaptchaEnabled && (
        <Recaptcha
          ref={recaptchaRef}
          siteKey={siteKey}
          theme="light"
          onToken={handleRecaptchaToken}
        />
      )}
    </section>
  );
}
